import React, { useEffect, useState } from 'react';
import { Alert, Button, Drawer, Space, Typography, message } from 'antd';
import { cacheOwner } from './profileCache';
import { pendingWaiting, retryWaitingPatient, syncWaitingPatients } from './waitingQueue';
import { preparePatients } from './preparePatients';
import { pendingProfileActions, retryProfileAction, synchronizeProfileActions } from './profileActions';
import { useClinicContext } from '../components/ClinicContext';
import { configureOfflineShell } from './offlineShell';
import { ensureOfflineAccess } from './access';
import { offlineRuntimeEnabled, profileOfflineEnabled } from './config';
import { useLanguage } from '../components/LanguageContext';

export default function OfflineWorkspaceStatus() {
  useEffect(() => { void configureOfflineShell().catch(() => {}); }, []);
  const { selectedClinicId, clinicScopeIds } = useClinicContext();
  const { language, isRTL } = useLanguage();
  const ar = language === 'ar';
  const [offline, setOffline] = useState(!navigator.onLine);
  const [rows, setRows] = useState<any[]>([]);
  const [actions, setActions] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState('');
  const [enabled, setEnabled] = useState(!!cacheOwner());
  useEffect(() => {
    let active = true;
    const refresh = async () => {
      setEnabled(!!cacheOwner());
      setOffline(!navigator.onLine);
      const items = await pendingWaiting();
      if (active) setRows(items);
      const profileItems = await pendingProfileActions();
      if (active) setActions(profileItems);
    };
    const tick = () => { void ensureOfflineAccess().catch(() => {}); void refresh().catch(() => {}); void syncWaitingPatients().catch(() => {}); void synchronizeProfileActions().catch(() => {}); };
    const readStatus = (event: Event) => {
      const data = (event as CustomEvent).detail;
      setDetail(data.trackingError
        ? (ar ? 'تعذر تحديث حالة المزامنة على الخادم. التعديلات المحلية محفوظة بأمان.' : 'Cloud sync status could not be updated. Your local changes are safely retained.')
        : data.storageError
          ? (ar ? 'تعذر حفظ أحد الأقسام التي تم تنزيلها على هذا الجهاز.' : 'A downloaded section could not be saved on this device.')
          : data.missing
            ? (ar ? 'بعض الأقسام لم يتم تنزيلها بعد. عدم ظهورها لا يعني أن سجل المريض فارغ.' : 'Some sections have not been downloaded yet. Missing data does not mean the patient history is empty.')
            : (ar
              ? `يتم عرض نسخة محفوظة بتاريخ ${new Date(data.savedAt).toLocaleString('ar-EG')}.`
              : `Showing a saved copy from ${new Date(data.savedAt).toLocaleString('en-US')}.`));
    };
    const warn = (event: BeforeUnloadEvent) => {
      // Warn only during an actual outage. Once connectivity is back, syncing
      // continues from IndexedDB and a normal reload must stay interruption-free.
      if (!navigator.onLine && (rows.length || actions.length)) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    tick();
    const timer = setInterval(tick, 15000);
    window.addEventListener('online', tick); window.addEventListener('offline', tick);
    window.addEventListener('offlineWaitingChanged', tick); window.addEventListener('doctorIdChanged', tick);
    window.addEventListener('offlineReadStatus', readStatus); window.addEventListener('beforeunload', warn);
    window.addEventListener('offlineProfileChanged', tick);
    return () => { active = false; clearInterval(timer);
      window.removeEventListener('online', tick); window.removeEventListener('offline', tick);
      window.removeEventListener('offlineWaitingChanged', tick); window.removeEventListener('doctorIdChanged', tick);
      window.removeEventListener('offlineReadStatus', readStatus); window.removeEventListener('beforeunload', warn);
      window.removeEventListener('offlineProfileChanged', tick);
    };
  }, [rows.length, actions.length, language, ar]);
  if (!offlineRuntimeEnabled || !profileOfflineEnabled || !localStorage.getItem('doctorId')) return null;
  // This is an outage status surface. Online operation should keep the normal
  // dashboard UI unchanged; pending work continues syncing in the background.
  if (!offline) return null;
  const panelStyle: React.CSSProperties = {
    position: 'fixed',
    left: 20,
    bottom: 20,
    zIndex: 1050,
    width: 'min(460px, calc(100vw - 40px))',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.16)',
    direction: isRTL ? 'rtl' : 'ltr',
    textAlign: isRTL ? 'right' : 'left',
  };
  if (!enabled) return <Alert style={panelStyle} type="warning" showIcon
    message={ar ? 'العمل بدون إنترنت غير جاهز لهذا الحساب' : 'Offline access is not ready for this account'}
    description={ar
      ? 'اتصل بالإنترنت وحدّث الصفحة مرة واحدة لتجهيز بيانات هذا الحساب للعمل بدون اتصال.'
      : 'Connect to the internet and refresh once to prepare this account for offline work.'} />;
  const totalPending = rows.length + actions.length;
  const offlineDescription = ar
    ? (totalPending
      ? `يمكنك متابعة العمل بشكل طبيعي. يوجد ${totalPending} تعديل محفوظ بأمان على هذا الجهاز، وسيتم رفعه تلقائياً عند عودة الإنترنت.`
      : 'يمكنك متابعة العمل بشكل طبيعي. سيتم حفظ أي تعديلات جديدة بأمان على هذا الجهاز ومزامنتها تلقائياً عند عودة الإنترنت.')
    : (totalPending
      ? `You can keep working. ${totalPending} ${totalPending === 1 ? 'change is' : 'changes are'} saved safely on this device and will sync automatically when the internet connection returns.`
      : 'You can keep working. New changes will be saved safely on this device and synced automatically when the internet connection returns.');
  const pendingBreakdown = ar
    ? `${rows.length} في قائمة الانتظار • ${actions.length} في ملفات المرضى بانتظار المزامنة`
    : `${rows.length} waiting-list ${rows.length === 1 ? 'change' : 'changes'} • ${actions.length} patient-profile ${actions.length === 1 ? 'change' : 'changes'} pending`;
  return <>
    <Alert style={panelStyle} type="warning" showIcon closable
      message={ar ? 'أنت تعمل الآن بدون اتصال بالإنترنت' : "You're currently offline"}
      description={<div>
        <div>{offlineDescription}</div>
        <div style={{ marginTop: 6, fontSize: 12 }}>{pendingBreakdown}</div>
        {detail && <div style={{ marginTop: 6, fontSize: 12, opacity: 0.8 }}>{detail}</div>}
      </div>}
      action={<Button onClick={() => setOpen(true)}>{ar ? 'عرض بيانات المزامنة' : 'View sync details'}</Button>} />
    <Drawer title={ar ? 'بيانات العمل بدون إنترنت' : 'Offline data'} open={open} onClose={() => setOpen(false)} width={520}
      styles={{ body: { direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' } }}>
      <Typography.Paragraph>{ar
        ? 'نزّل سجلات المرضى للعيادة المختارة قبل انقطاع الإنترنت. يمكنك متابعة العمل أثناء الانقطاع، وستتم مزامنة التعديلات تلقائياً عند عودة الاتصال. لا تمسح بيانات الموقع أثناء وجود تعديلات بانتظار المزامنة.'
        : 'Download patient records for the selected clinic before an outage. You can keep working while offline, and changes will sync automatically when the connection returns. Do not clear site data while changes are pending.'}</Typography.Paragraph>
      <Button loading={busy} disabled={offline || !selectedClinicId} onClick={async () => {
        setBusy(true);
        try {
          const result = await preparePatients(localStorage.getItem('doctorId') || '', selectedClinicId || '', clinicScopeIds || [], setDetail);
          setDetail(ar
            ? `تم تنزيل ${result.count} سجل مريض. تعذر تنزيل ${result.failed} قسم.`
            : `${result.count} patient records downloaded. ${result.failed} sections could not be downloaded.`);
        } catch (error: any) { message.error(error.message); }
        finally { setBusy(false); }
      }}>{ar ? 'تنزيل سجلات المرضى' : 'Download patient records'}</Button>
      <Typography.Paragraph>{detail}</Typography.Paragraph>
      {actions.map(action => <Space key={action.key} direction="vertical" style={{ display: 'flex', marginTop: 16 }}>
        <Typography.Text strong>{action.patientName}</Typography.Text>
        <Typography.Text>{action.status === 'attention'
          ? (ar ? 'يحتاج إلى مراجعة — توقفت مزامنة هذا المريض' : 'Needs review — synchronization stopped for this patient')
          : (ar ? 'تعديل في ملف المريض بانتظار المزامنة' : 'Patient-profile change pending sync')}</Typography.Text>
        <Typography.Text type="secondary">{ar ? 'رقم العملية' : 'Action'}: {action.operationId}</Typography.Text>
        <Typography.Paragraph copyable style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(action.request.body, null, 2)}</Typography.Paragraph>
        {action.error && <Typography.Text type="danger">{action.error}</Typography.Text>}
        <Button onClick={() => { void retryProfileAction(action).catch(() => message.error(ar ? 'تعذرت إعادة المحاولة. البيانات المحلية ما زالت محفوظة.' : 'Could not retry. Local data is retained.')); }}>{ar ? 'إعادة محاولة الحفظ' : 'Retry this save'}</Button>
      </Space>)}
      {rows.map(action => <Space key={action.key} direction="vertical" style={{ display: 'flex', marginTop: 16 }}>
        <Typography.Text strong>{action.patient.patient_name}</Typography.Text>
        <Typography.Text>{action.kind === 'reminder'
          ? (ar ? 'تذكير بزيارة' : 'Visit reminder')
          : (ar ? 'إضافة إلى قائمة الانتظار' : 'Waiting-list addition')} — {action.status === 'attention'
            ? (ar ? 'يحتاج إلى مراجعة' : 'Needs attention')
            : (ar ? 'بانتظار المزامنة' : 'Pending sync')}</Typography.Text>
        {action.error && <Typography.Text type="danger">{action.error}</Typography.Text>}
        <Button onClick={() => { void retryWaitingPatient(action).catch(() => message.error(ar ? 'تعذرت إعادة المحاولة. النسخة المحلية ما زالت محفوظة.' : 'Could not retry. The local copy is retained.')); }}>{ar ? 'إعادة محاولة الإضافة' : 'Retry this addition'}</Button>
      </Space>)}
    </Drawer>
  </>;
}
